import { useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

/**
 * Category / department image upload. Mirrors useUploadProductImage: multipart
 * POST to the products service (→ S3), stored on categories.image_url /
 * departments.image_url and synced to POS where it shows on the tile.
 *
 * Uses productsApi.postForm (not a raw fetch) so the upload inherits the shared
 * api-client's 403 NO_TENANT refresh-and-retry: when the access token carries a
 * stale/empty tenantId, a raw fetch would 403 outright ("Image upload failed")
 * while every other call on the page silently recovers.
 */
export function useUploadCategoryImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ categoryId, file }: { categoryId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return productsApi.postForm(`/categories/${categoryId}/image`, formData);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

export function useUploadDepartmentImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ departmentId, file }: { departmentId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      return productsApi.postForm(`/departments/${departmentId}/image`, formData);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
  });
}
